// Selection registry helpers + domain resolver (Stage 5 — TASKS-UI.md).
//
// Registry lives at `params.selections` + `params.selectionOrder` +
// `params.nextSelectionSeq`. Each entry is a discriminated `BoxSelection`
// or `SphereSelection` keyed by a unified id (`sel-1`, `sel-2`, …); the
// `kind` field gives typed narrowing where it matters (render, editor).
//
// `resolveDomainBounds` is the single source of truth every primitive uses
// to convert a `<x>Domain` string (`'full'` or a `SelectionId`) into a
// concrete `ResolvedDomain` shape. `isOutsideDomain` is the dual helper for
// the field-line tracer's bounds check (CPU + worker share it).
//
// Subscriptions: callers listen on `params.selectionOrder` for "registry
// shape changed" (add/remove/reorder) and on `params.selections.<id>` for
// individual edits. The store's path-prefix notify makes both fire when we
// mutate via `store.set`. Deletes mutate the underlying object directly
// (the store has no "unset"), then we publish `selectionOrder` to fan out.

import { params, store } from './instance';
import type {
  BoxSelection,
  PrimitiveKey,
  Selection,
  SelectionId,
  SphereSelection,
} from './schema';
import type { GridState } from '../app/context';

/** Domain-strip consumers. The four render primitives plus the Reductions pane,
 *  which reuses the strip as its one-reduction-per-domain selector. `reductions`
 *  is deliberately *not* a `PrimitiveKey` — it has no scene render. */
export type DomainScope = PrimitiveKey | 'reductions';

/** Path to the per-scope Domain field on params. */
const PRIMITIVE_DOMAIN_KEY: Record<DomainScope, 'slicesDomain' | 'volumeDomain' | 'contourDomain' | 'fieldLinesDomain' | 'reductionsDomain'> = {
  slices: 'slicesDomain',
  volume: 'volumeDomain',
  contour: 'contourDomain',
  fieldlines: 'fieldLinesDomain',
  reductions: 'reductionsDomain',
};

export function primitiveDomainKey(primitive: DomainScope): typeof PRIMITIVE_DOMAIN_KEY[DomainScope] {
  return PRIMITIVE_DOMAIN_KEY[primitive];
}

// ─── id minting ────────────────────────────────────────────────────────────

function mintId(): SelectionId {
  const seq = params.nextSelectionSeq;
  store.set('params.nextSelectionSeq', seq + 1);
  return `sel-${seq}`;
}

function nextLabel(kind: 'box' | 'sphere'): string {
  let n = 0;
  for (const id of params.selectionOrder) {
    const s = params.selections[id];
    if (s && s.kind === kind) n++;
  }
  return kind === 'box' ? `Box ${n + 1}` : `Sphere ${n + 1}`;
}

// ─── defaults from grid ────────────────────────────────────────────────────

function defaultBoxBounds(grid: GridState): { lower: [number, number, number]; upper: [number, number, number] } {
  const fx = (lo: number, hi: number, t: number): number => lo + (hi - lo) * t;
  return {
    lower: [fx(grid.xMin, grid.xMax, 0.25), fx(grid.yMin, grid.yMax, 0.25), fx(grid.zMin, grid.zMax, 0.25)],
    upper: [fx(grid.xMin, grid.xMax, 0.75), fx(grid.yMin, grid.yMax, 0.75), fx(grid.zMin, grid.zMax, 0.75)],
  };
}

function defaultSphere(grid: GridState): { center: [number, number, number]; radius: number } {
  const cx = (grid.xMin + grid.xMax) / 2;
  const cy = (grid.yMin + grid.yMax) / 2;
  const cz = (grid.zMin + grid.zMax) / 2;
  const ext = Math.min(grid.xMax - grid.xMin, grid.yMax - grid.yMin, grid.zMax - grid.zMin);
  return { center: [cx, cy, cz], radius: Math.max(0.5, ext * 0.25) };
}

// ─── clamping ──────────────────────────────────────────────────────────────

function clampValue(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

function clampBox(lower: readonly [number, number, number], upper: readonly [number, number, number], grid: GridState): { lower: [number, number, number]; upper: [number, number, number] } {
  const lx = clampValue(lower[0], grid.xMin, grid.xMax);
  const ly = clampValue(lower[1], grid.yMin, grid.yMax);
  const lz = clampValue(lower[2], grid.zMin, grid.zMax);
  const ux = clampValue(upper[0], grid.xMin, grid.xMax);
  const uy = clampValue(upper[1], grid.yMin, grid.yMax);
  const uz = clampValue(upper[2], grid.zMin, grid.zMax);
  return {
    lower: [Math.min(lx, ux), Math.min(ly, uy), Math.min(lz, uz)],
    upper: [Math.max(lx, ux), Math.max(ly, uy), Math.max(lz, uz)],
  };
}

function clampSphere(center: readonly [number, number, number], radius: number, grid: GridState): { center: [number, number, number]; radius: number } {
  return {
    center: [
      clampValue(center[0], grid.xMin, grid.xMax),
      clampValue(center[1], grid.yMin, grid.yMax),
      clampValue(center[2], grid.zMin, grid.zMax),
    ],
    radius: Math.max(1e-6, radius),
  };
}

// ─── CRUD ──────────────────────────────────────────────────────────────────

export interface CreateBoxOpts {
  lower?: readonly [number, number, number];
  upper?: readonly [number, number, number];
  label?: string;
  focus?: boolean;
}

export function createBoxSelection(grid: GridState, opts: CreateBoxOpts = {}): SelectionId {
  const id = mintId();
  const defaults = defaultBoxBounds(grid);
  const seed = clampBox(opts.lower ?? defaults.lower, opts.upper ?? defaults.upper, grid);
  const selection: BoxSelection = {
    kind: 'box',
    id,
    label: opts.label ?? nextLabel('box'),
    lower: seed.lower,
    upper: seed.upper,
  };
  store.set(`params.selections.${id}`, selection);
  store.set('params.selectionOrder', [...params.selectionOrder, id]);
  if (opts.focus !== false) store.set('params.focusedSelectionId', id);
  return id;
}

export interface CreateSphereOpts {
  center?: readonly [number, number, number];
  radius?: number;
  keep?: 'inside' | 'outside';
  label?: string;
  focus?: boolean;
}

export function createSphereSelection(grid: GridState, opts: CreateSphereOpts = {}): SelectionId {
  const id = mintId();
  const defaults = defaultSphere(grid);
  const seed = clampSphere(opts.center ?? defaults.center, opts.radius ?? defaults.radius, grid);
  const selection: SphereSelection = {
    kind: 'sphere',
    id,
    label: opts.label ?? nextLabel('sphere'),
    center: seed.center,
    radius: seed.radius,
    keep: opts.keep ?? 'inside',
  };
  store.set(`params.selections.${id}`, selection);
  store.set('params.selectionOrder', [...params.selectionOrder, id]);
  if (opts.focus !== false) store.set('params.focusedSelectionId', id);
  return id;
}

/**
 * Flat permissive patch shape. Callers don't tag patches with `kind` (the
 * UI editor for a known box only emits `lower`/`upper`, etc.), so we accept
 * any union of shape-specific fields and silently drop those that don't
 * apply to the current selection's kind. Field types stay precise — no
 * `unknown`, no `any`, no cast in the implementation.
 */
export type SelectionPatch = {
  label?: string;
  lower?: readonly [number, number, number];
  upper?: readonly [number, number, number];
  center?: readonly [number, number, number];
  radius?: number;
  keep?: 'inside' | 'outside';
};

export function updateSelection(id: SelectionId, patch: SelectionPatch, grid: GridState): void {
  const cur = params.selections[id];
  if (!cur) return;
  let next: Selection;
  if (cur.kind === 'box') {
    const clamped = clampBox(patch.lower ?? cur.lower, patch.upper ?? cur.upper, grid);
    next = { ...cur, label: patch.label ?? cur.label, lower: clamped.lower, upper: clamped.upper };
  } else {
    const clamped = clampSphere(patch.center ?? cur.center, patch.radius ?? cur.radius, grid);
    next = { ...cur, label: patch.label ?? cur.label, center: clamped.center, radius: clamped.radius, keep: patch.keep ?? cur.keep };
  }
  store.set(`params.selections.${id}`, next);
}

export function removeSelection(id: SelectionId): void {
  if (!params.selections[id]) return;
  // Mutate in place — the store has no "unset". Then publish order to fan out.
  const reg = store.state.params.selections as Record<string, Selection>;
  delete reg[id];
  store.set('params.selectionOrder', params.selectionOrder.filter((x) => x !== id));
  // Reset any scope whose Domain referenced this id.
  for (const p of Object.keys(PRIMITIVE_DOMAIN_KEY) as DomainScope[]) {
    const key = PRIMITIVE_DOMAIN_KEY[p];
    if (params[key] === id) store.set(`params.${key}`, 'full');
  }
  if (params.focusedSelectionId === id) store.set('params.focusedSelectionId', null);
}

export function setFocus(id: SelectionId | null): void {
  if (id !== null && !params.selections[id]) return;
  store.set('params.focusedSelectionId', id);
}

export function setDomain(primitive: DomainScope, selectionId: SelectionId | 'full'): void {
  // Validate selection id; unknown ids fall through to 'full'.
  if (selectionId !== 'full' && !params.selections[selectionId]) selectionId = 'full';
  store.set(`params.${PRIMITIVE_DOMAIN_KEY[primitive]}`, selectionId);
}

// ─── domain resolver ───────────────────────────────────────────────────────

export type ResolvedDomain =
  | { kind: 'full' }
  | { kind: 'box'; lower: readonly [number, number, number]; upper: readonly [number, number, number] }
  | { kind: 'sphere'; center: readonly [number, number, number]; radius: number; keep: 'inside' | 'outside' };

/**
 * Resolve a `<x>Domain` value (`'full'` or a `SelectionId`) to a concrete
 * `ResolvedDomain`. Unknown ids degrade to `'full'`.
 */
export function resolveDomainBounds(
  selections: Record<SelectionId, Selection>,
  domainId: string,
): ResolvedDomain {
  if (!domainId || domainId === 'full') return { kind: 'full' };
  const s = selections[domainId];
  if (!s) return { kind: 'full' };
  if (s.kind === 'box') return { kind: 'box', lower: s.lower, upper: s.upper };
  return { kind: 'sphere', center: s.center, radius: s.radius, keep: s.keep };
}

/**
 * True when (x, y, z) is outside the resolved domain. `'full'` always returns
 * false. Used by the slice CPU mask, the field-line tracer's bounds check,
 * and the contour main-thread sphere filter.
 */
export function isOutsideDomain(x: number, y: number, z: number, domain: ResolvedDomain): boolean {
  if (domain.kind === 'full') return false;
  if (domain.kind === 'box') {
    return (
      x < domain.lower[0] || x > domain.upper[0] ||
      y < domain.lower[1] || y > domain.upper[1] ||
      z < domain.lower[2] || z > domain.upper[2]
    );
  }
  const dx = x - domain.center[0];
  const dy = y - domain.center[1];
  const dz = z - domain.center[2];
  const inside = (dx * dx + dy * dy + dz * dz) <= domain.radius * domain.radius;
  return domain.keep === 'inside' ? !inside : inside;
}

/** Centroid of a selection (midpoint for box, center for sphere). */
export function selectionCentroid(s: Selection): { x: number; y: number; z: number } {
  if (s.kind === 'box') {
    return {
      x: (s.lower[0] + s.upper[0]) / 2,
      y: (s.lower[1] + s.upper[1]) / 2,
      z: (s.lower[2] + s.upper[2]) / 2,
    };
  }
  return { x: s.center[0], y: s.center[1], z: s.center[2] };
}
